// apps/vault-shell/src/app/app.config.ts
import {
  ApplicationConfig,
  importProvidersFrom,
  inject,
  provideAppInitializer,
  provideBrowserGlobalErrorListeners,
  Provider,
} from '@angular/core';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideRouter } from '@angular/router';
import { provideHttpClient, withFetch, withInterceptors } from '@angular/common/http';
import {
  API_BASE_URL_TOKEN,
  AUTH_CONFIG_TOKEN,
  TranslationService,
} from '@nebula-shared/data-access';
import {
  DEFAULT_NEBULA_UI_CONFIG,
  provideNebulaCoreIcons,
  NEBULA_UI_CONFIG,
  NebulaNavigationService,
} from 'nebula-ui-kit';
import {
  Activity,
  BarChart3,
  Cloud,
  FileSliders,
  Folder,
  FolderSearch,
  FolderArchive,
  FolderGit2,
  FolderKanban,
  FolderLock,
  FolderSync,
  FolderUp,
  Gauge,
  HardDrive,
  House,
  Key,
  LUCIDE_ICONS,
  LucideIconProvider,
  SearchCheck,
  Server,
  SquareKanban,
  UserKey,
  UserCheck,
  Users,
  UserStar,
  CircleArrowLeft,
  Tags,
  Shield,
  BetweenHorizontalEnd,
  Building2,
  ArchiveRestore,
  Archive,
  File,
  FileText,
  Image,
  Table,
  Presentation,
  Video,
  Music,
  Eye,
  ChevronLeft,
  ChevronRight,
  MessageSquareDot,
  Vault,
  ArrowLeft,
  Lock,
  LockOpen,
  ZoomIn,
  ZoomOut,
  Settings,
  CircleCheck,
  Calendar,
  Clock,
  User,
  Globe,
  MapPin,
  Info,
  ChevronDown,
  CircleX,
  CircleAlert,
  RefreshCw,
  Download,
  SquareCheck,
  UserMinus,
} from 'lucide-angular';
import { appRoutes } from './app.routes';
import { environment } from '../enviroments/environment';
import {
  createApiVaultInterceptor,
  errorInterceptor,
  tokenInterceptor,
  VAULT_CONFIG,
  VAULT_BACKEND_NOTIFICATIONS,
  INGEST_MODULE_ENV,
  getEnvProfile,
  resolveVaultApiUrl,
  API_BASE_URL_TOKEN as VAULT_API_BASE_URL_TOKEN,
  AUTH_CONFIG_TOKEN as VAULT_AUTH_CONFIG_TOKEN,
  type VaultBackendNotificationsPort,
  type VaultBackendNotificationRow,
} from '@nebula/shared-vault/data-access';

import {
  NOTIFICATION_PANEL_OPENER,
  type NotificationPanelOpenOptions,
} from '@nebula-vault/layout';
import { NotificacionDialogComponent } from '@nebula-shared/ui';
import { NotificationsService, VAULT_ADMIN_USER_TENANTS_API_ENABLED } from '@nebula/vault';
import { map } from 'rxjs/operators';

function mapBackendTipo(t?: string | null): VaultBackendNotificationRow['tipo'] {
  const x = (t || '').toLowerCase();
  if (x === 'error' || x === 'danger') return 'error';
  if (x === 'warning' || x === 'warn') return 'warning';
  if (x === 'success') return 'success';
  return 'info';
}

function resolveApiBaseUrl(): string {
  if (typeof window !== 'undefined') {
    return getEnvProfile().apiBaseUrl;
  }
  return environment.API_BASE_URL || 'https://api-dev.centricasoluciones.com';
}

/** Mismo base URL para @nebula-shared y @nebula/shared-vault (tokens distintos). */
function provideApiBaseUrlTokens(): Provider[] {
  return [
    { provide: API_BASE_URL_TOKEN, useFactory: resolveApiBaseUrl },
    { provide: VAULT_API_BASE_URL_TOKEN, useFactory: resolveApiBaseUrl },
  ];
}

function provideAuthConfigTokens(): Provider[] {
  const factory = () => ({ baseUrl: resolveApiBaseUrl() });
  return [
    { provide: AUTH_CONFIG_TOKEN, useFactory: factory },
    { provide: VAULT_AUTH_CONFIG_TOKEN, useFactory: factory },
  ];
}

function provideVaultConfig(): Provider {
  return {
    provide: VAULT_CONFIG,
    useFactory: () => {
      const apiUrl = resolveVaultApiUrl() || '/api';
      const envProfile = getEnvProfile();

      return {
        apiUrl,
        loginUrl: envProfile.loginUrl,
        sessionInactivityMinutes: 24 * 60,
        sessionWarningMinutes: 5,
      };
    },
  };
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideAnimations(),
    importProvidersFrom(MatDialogModule),
    provideBrowserGlobalErrorListeners(),
    provideRouter(appRoutes),

    provideHttpClient(
      withFetch(),
      withInterceptors([
        createApiVaultInterceptor(),
        tokenInterceptor,
        errorInterceptor,
      ]),
    ),

    ...provideNebulaCoreIcons(),

    NebulaNavigationService,

    // i18n: precarga idiomas + bundle (simappe-admin) antes del primer render.
    provideAppInitializer(() => inject(TranslationService).init()),

    {
      provide: NEBULA_UI_CONFIG,
      useValue: {
        ...DEFAULT_NEBULA_UI_CONFIG,
        navStorageKey: 'nebula_ui_nav_v1',
        searchPlaceholder: 'Buscar',
      },
    },

    {
      provide: LUCIDE_ICONS,
      useFactory: () =>
        new LucideIconProvider({
          CircleArrowLeft,
          Tags,
          Shield,
          BetweenHorizontalEnd,
          Building2,
          ArchiveRestore,
          Archive,
          FolderUp,
          ChevronLeft,
          ChevronRight,
          BarChart3,
          Cloud,
          Activity,
          Key,
          Server,
          FolderLock,
          FolderSync,
          FolderKanban,
          FolderGit2,
          SearchCheck,
          SquareKanban,
          Users,
          UserCheck,
          UserKey,
          HardDrive,
          FolderArchive,
          House,
          Folder,
          FolderSearch,
          FileSliders,
          UserStar,
          Gauge,
          File,
          FileText,
          Image,
          Table,
          Presentation,
          Video,
          Music,
          Eye,
          Vault,
          MessageSquareDot,
          ArrowLeft,
          Lock,
          LockOpen,
          ZoomIn,
          ZoomOut,
          Settings,
          CircleCheck,
          Calendar,
          Clock,
          User,
          Globe,
          MapPin,
          Info,
          ChevronDown,
          CircleX,
          CircleAlert,
          RefreshCw,
          Download,
          SquareCheck,
          UserMinus,
        }),
      multi: true,
    },

    ...provideApiBaseUrlTokens(),
    ...provideAuthConfigTokens(),
    provideVaultConfig(),

    {
      provide: VAULT_ADMIN_USER_TENANTS_API_ENABLED,
      useValue: environment.vaultAdminUserTenantsApiEnabled ?? true,
    },

    {
      provide: INGEST_MODULE_ENV,
      useValue: {
        previewMode: environment?.ingestUiMock ?? false,
      },
    },

    {
      provide: VAULT_BACKEND_NOTIFICATIONS,
      useFactory: (): VaultBackendNotificationsPort => {
        const api = inject(NotificationsService);
        return {
          list: (unreadOnly) =>
            api.list(unreadOnly).pipe(
              map((rows) =>
                rows.map((n) => ({
                  id: n.id,
                  titulo: n.title,
                  mensaje: n.message,
                  fecha: n.created_at,
                  leida: Boolean(n.read ?? n.is_read),
                  tipo: mapBackendTipo(n.type),
                })),
              ),
            ),
          markAsRead: (id) => api.markAsRead(id),
          markAllAsRead: () => api.markAllAsRead(),
        };
      },
    },

    {
      provide: NOTIFICATION_PANEL_OPENER,
      useFactory: () => {
        const dialog = inject(MatDialog);
        return (opts: NotificationPanelOpenOptions) => {
          const gapPx = 8;
          const rect = opts.anchor?.getBoundingClientRect();
          const position =
            rect != null && Number.isFinite(rect.bottom)
              ? {
                  top: `${rect.bottom + gapPx}px`,
                  right: `${window.innerWidth - rect.right}px`,
                }
              : undefined;

          const panelClass: string[] = [];
          if (opts.modoOscuro) {
            panelClass.push('notificacion-dialog-dark');
          }
          if (position) {
            panelClass.push('notificacion-dialog-below-header');
          }

          return dialog
            .open(NotificacionDialogComponent, {
              width: '420px',
              maxWidth: '95vw',
              maxHeight: '90vh',
              data: { modoOscuro: opts.modoOscuro },
              position,
              panelClass: panelClass.length ? panelClass : undefined,
            })
            .afterClosed();
        };
      },
    },
  ],
};
